import React, { useRef, useState } from 'react';
import { Button, Checkbox, Empty, Space, Tag, Tooltip, Typography } from 'antd';
import { DownloadOutlined, SwapOutlined, UploadOutlined } from '@ant-design/icons';
import { assetDisplayNames, formatBytes } from '../ingestionHelpers';
import { AssetImage, activeDiffuseOf } from './VariantAssetViewer';
import { GalleryPickerModal } from './GalleryPickerModal';

const { Text } = Typography;

/**
 * A texture variant's asset: the one image that is its texture, with a way to
 * upload another or pick one from the product gallery (the Product images tab
 * holds the rest of the images), taken as is or run through texture generation.
 */
export function TextureAssetPanel({ itemId, variantId, assets, onUpload, onReplace, onGenerateFrom, onDownload }) {
    const inputRef = useRef(null);
    const [picking, setPicking] = useState(false);
    const [asIs, setAsIs] = useState(true);
    const texture = activeDiffuseOf(assets);
    const name = texture ? assetDisplayNames(assets)[texture.id] : null;

    return (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div
                style={{
                    width: 320,
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fafafa',
                    borderRadius: 6,
                    padding: 8,
                }}
            >
                {texture ? (
                    <AssetImage key={texture.id} itemId={itemId} assetId={texture.id} />
                ) : (
                    <Empty description="No texture yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </div>
            <Space direction="vertical" size="small">
                {texture && (
                    <Space wrap size={6}>
                        <Tag color="success" style={{ marginInlineEnd: 0 }}>texture</Tag>
                        <Text style={{ fontSize: 12 }}>{name}</Text>
                        {texture.byteSize != null && <Text type="secondary" style={{ fontSize: 12 }}>{formatBytes(texture.byteSize)}</Text>}
                        <Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => onDownload(texture, name)} />
                    </Space>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) (asIs ? onUpload(file) : onGenerateFrom({ file }));
                        e.target.value = '';
                    }}
                />
                <Button icon={<UploadOutlined />} onClick={() => inputRef.current?.click()}>
                    Upload texture image
                </Button>
                <Button icon={<SwapOutlined />} onClick={() => setPicking(true)}>
                    Replace texture image
                </Button>
                <Tooltip title="Ticked: the uploaded or picked image is the texture, unchanged. Unticked: the assets step runs on it (seamless swatch, artwork extraction, pillow cover) and the result is the texture.">
                    <Checkbox checked={asIs} onChange={(e) => setAsIs(e.target.checked)}>
                        Use texture as is
                    </Checkbox>
                </Tooltip>
            </Space>
            <GalleryPickerModal
                itemId={itemId}
                variantId={variantId}
                open={picking}
                single
                title="Replace the texture image"
                okLabel="Use as texture"
                onPick={([url]) => url && (asIs ? onReplace(url) : onGenerateFrom({ url }))}
                onClose={() => setPicking(false)}
            />
        </div>
    );
}
